import { AxiosError } from 'axios';
import i18n, { t } from 'i18next';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { makePersistable } from 'mobx-persist-store';

import { ETADeliveryMethodType } from '~/api/ETADeliveryMethodType';
import { type CheckPromocodeResponse, OrderRequests } from '~/api/Order';
import { PromocodesRequests } from '~/api/Promocodes';
import {
  ReferralProgramRequests,
  type RewardStatusResponse,
} from '~/api/ReferralProgram';
import type { ApiErrorResponse } from '~/api/Requests';
import { Promocode } from '~/entities/Promocode';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { mainStore, orderStore, type UserStore } from '~/stores';
import type {
  ICartStore,
  Promocode as ReferralPromocode,
} from '~/stores/CartStore/interfaces';
import { notSSRFormatPriceWithCurrency } from '~/utils/formaters';
import { clearStoredPromocode, getStoredPromocode } from '~/utils/referralLink';

import { PICKUP_ALCOHOL_COUPON_CODE, PRESENT_PROMOCODES } from '../constants';
import type { BaseStore } from '../interfaces/BaseStore';
import { storage as localStorage } from '../LocalStorage';
import { MetaStore } from '../shared/MetaStore';
import { InfiniteQuery, Query } from '../shared/Query';

import {
  ApplyPromocodeCallerSource,
  PromocodesTab,
  type ProposedPromocode,
} from './interfaces';
import { truncateItemName } from './utils';

export class PromotionStore implements BaseStore {
  private readonly _abortController: AbortController = new AbortController();

  promocodesTab: PromocodesTab = PromocodesTab.AVAILABLE;

  private static readonly PAGINATION_SIZE = 10;
  private static readonly STALE_TIME = 0;
  private static readonly STALE_TIME_REFERRAL = 5 * 60 * 1000;
  private static readonly INITIAL_PAGE_PARAM = 1;

  private readonly _activePromocodesQuery;

  private readonly _inactivePromocodesQuery;

  private readonly _usedPromocodesQuery;

  private readonly _rewardedPromocodesQuery;

  private _createPromocodesQuery(
    queryKeyPrefix: string,
    status: PromocodesTab,
    additionalEnabledTabs: PromocodesTab[] = [],
  ) {
    return new InfiniteQuery({
      queryFn: ({ pageParam, queryKey: [, userId] }) => {
        if (!userId || typeof userId !== 'string') {
          throw new Error('Invalid query key');
        }

        return PromocodesRequests.getPromocodes(userId, status, {
          pagination: {
            size: PromotionStore.PAGINATION_SIZE,
            current: pageParam,
          },
        });
      },
      enabled: ({ queryKey }) =>
        !!queryKey[1] &&
        (queryKey[2] === status ||
          additionalEnabledTabs.includes(queryKey[2] as PromocodesTab)),
      select: (data) =>
        data.pages
          .flatMap((page) =>
            page.data.map(
              (promocode) =>
                new Promocode({
                  ...promocode,
                  status,
                }),
            ),
          )
          .sort(Promocode.sort),
      initialPageParam: PromotionStore.INITIAL_PAGE_PARAM,
      staleTime: PromotionStore.STALE_TIME,
      options: () => ({
        queryKey: [
          queryKeyPrefix,
          this.userStore.personalData.id,
          this.promocodesTab,
        ],
      }),
      getNextPageParam: (lastPage) => {
        const { pagination } = lastPage;
        const hasNext = pagination.current * pagination.size < pagination.total;
        return hasNext ? pagination.current + 1 : undefined;
      },
    });
  }

  readonly applyPromocodeMeta: MetaStore = new MetaStore();

  readonly usedPromocodesMap: Map<string, string[]> = new Map();
  readonly proposedPromocodes: Set<ProposedPromocode> = new Set();

  unseenAwardedPromocodesCount: number = 0;
  promocodeRewardStatus: RewardStatusResponse = {
    new: 0,
    total: 0,
  };

  promocode: ReferralPromocode = {
    coupon: null,
    value: '',
    errorType: null,
    message: '',
    success: null,
    discountLimit: null,
  };

  constructor(
    protected catalogStore: ICartStore,
    protected userStore: UserStore,
  ) {
    makeObservable(this, {
      promocode: observable,
      promocodesTab: observable,
      unseenAwardedPromocodesCount: observable,
      promocodeRewardStatus: observable,
      usedPromocodesMap: observable,
      proposedPromocodes: observable,
      activePromocodes: computed,
      inactivePromocodes: computed,
      usedPromocodes: computed,
      referralPromocodes: computed,
      historyPromocodes: computed,
      isActivePromocodesLoading: computed,
      isInactivePromocodesLoading: computed,
      isUsedPromocodesLoading: computed,
      promocodes: computed,
      isPromocodesLoading: computed,
      canLoadMorePromocodes: computed,
      isFetchingNextPage: computed,
      fetchNextPage: action.bound,
      applyPromocode: action.bound,
      resetPromocode: action.bound,
      removeUsedPromocode: action.bound,
      addUsedPromocodes: action.bound,
      loadStoredPromoCodes: action.bound,
      getUnseenPromoRewards: action.bound,
      getRewardedPromocodesList: action.bound,
      getRewardedPromocodesStatus: action.bound,
      setPromocodesTab: action.bound,
    });
    makePersistable(this, {
      name: 'PromotionStore',
      properties: ['promocode', 'usedPromocodesMap'],
      storage: localStorage,
    });

    this._activePromocodesQuery = this._createPromocodesQuery(
      'promocodesActive',
      PromocodesTab.AVAILABLE,
    );

    this._inactivePromocodesQuery = this._createPromocodesQuery(
      'promocodesInactive',
      PromocodesTab.INACTIVE,
      [PromocodesTab.HISTORY],
    );

    this._usedPromocodesQuery = new InfiniteQuery({
      queryFn: ({ pageParam }) =>
        OrderRequests.getOrdersWithPromocodes({
          page: pageParam,
          limit: PromotionStore.PAGINATION_SIZE,
        }),
      enabled: ({ queryKey }) =>
        !!queryKey[1] &&
        (queryKey[2] === PromocodesTab.USED ||
          queryKey[2] === PromocodesTab.HISTORY),
      select: (data) =>
        data.pages.flatMap(({ data }) =>
          data.map(
            (order) =>
              new Promocode({
                ...order.promocode_data,
                order,
                status: PromocodesTab.USED,
              }),
          ),
        ),
      initialPageParam: PromotionStore.INITIAL_PAGE_PARAM,
      staleTime: PromotionStore.STALE_TIME,
      options: () => ({
        queryKey: [
          'promocodesUsed',
          this.userStore.personalData.id,
          this.promocodesTab,
        ],
      }),
      getNextPageParam: (lastPage) => {
        const { pagination } = lastPage;
        const hasNext = pagination.page * pagination.limit < pagination.total;
        return hasNext ? pagination.page + 1 : undefined;
      },
    });

    this._rewardedPromocodesQuery = new Query({
      queryFn: ({ queryKey: [, userId] }) => {
        if (!userId || typeof userId !== 'string') {
          throw new Error('Invalid query key');
        }
        return ReferralProgramRequests.getCustomerRewards(userId);
      },
      enabled: ({ queryKey }) =>
        !!queryKey[1] && queryKey[2] === PromocodesTab.REFERRAL,
      staleTime: PromotionStore.STALE_TIME_REFERRAL,
      options: () => ({
        queryKey: [
          'rewardedPromocodes',
          this.userStore.personalData.id,
          this.promocodesTab,
        ],
      }),
    });
  }

  static generateErrorPromocode(
    code: string,
    message = 'errors:promocodeError',
    messageArgs?: Record<string, unknown>,
  ): ReferralPromocode {
    return {
      coupon: null,
      value: code,
      errorType: 'error',
      message: i18n.t(message, messageArgs),
      success: false,
      discountLimit: null,
    };
  }

  async applyPromocode(
    code: string,
    source: ApplyPromocodeCallerSource,
  ): Promise<boolean> {
    this.applyPromocodeMeta.setLoading(true);
    this.applyPromocodeMeta.setError(false);

    try {
      const response = await OrderRequests.checkPromocode(code, {
        signal: this._abortController.signal,
      });

      const validated = this.validatePromocode(response, code);

      runInAction(() => {
        // This assign will trigger useEffect that calls `calculateCart`
        // And then `calculateCart` will execute `handleCalculateWithPromocodeResponse` handler.
        this.promocode = validated;
      });
      this.applyPromocodeMeta.setLoading(false);
    } catch (error) {
      const promocodeWithError = this._handleError(code, error, source);
      this.applyPromocodeMeta.setError(true);
      runInAction(() => {
        this.promocode = promocodeWithError;
      });
    } finally {
      if (!this.promocode.coupon) {
        this.applyPromocodeMeta.setLoading(false);
      }
    }

    if (!this.promocode.coupon) {
      this.removeUsedPromocode(code);
      return false;
    }

    this._emitUsedPromocodeEvent();
    return true;
  }

  async getUnseenPromoRewards() {
    const userId = this.userStore.personalData.id;

    if (!userId) {
      return;
    }

    try {
      const response =
        await ReferralProgramRequests.getRewardsNotifications(userId);
      // Receiving response from the server as text/plain. Safely extract value as number
      const unseenPromocodesCount = Number(response);

      runInAction(() => {
        if (isNaN(unseenPromocodesCount)) {
          this.unseenAwardedPromocodesCount = 0;
          return;
        }

        this.unseenAwardedPromocodesCount = unseenPromocodesCount;
        this.promocodeRewardStatus.new += unseenPromocodesCount;
        this.promocodeRewardStatus.total += unseenPromocodesCount;
      });

      return unseenPromocodesCount;
    } catch (error: unknown) {
      await mainStore.errorHandler(error as AxiosError<ApiErrorResponse>, '');
    }
  }

  async getRewardedPromocodesList() {
    const userId = this.userStore.personalData.id;

    if (!userId) {
      return;
    }

    try {
      await this._rewardedPromocodesQuery.refetch();

      runInAction(() => {
        this.promocodeRewardStatus.new = 0;
      });
    } catch (error: unknown) {
      await mainStore.errorHandler(error as AxiosError<ApiErrorResponse>, '');
    }
  }

  async getRewardedPromocodesStatus() {
    const userId = this.userStore.personalData.id;

    if (!userId) {
      return;
    }

    try {
      const response = await ReferralProgramRequests.getRewardStatus(userId);

      runInAction(() => {
        this.promocodeRewardStatus = response;
      });
    } catch (error: unknown) {
      await mainStore.errorHandler(error as AxiosError<ApiErrorResponse>, '');
    }
  }

  addUsedPromocodes(promocode: string) {
    const { isAuthorized, personalData } = this.userStore;

    if (!isAuthorized || !personalData.id) {
      return;
    }

    const userId = personalData.id;
    const usedByUser = this.usedPromocodesMap.get(userId);

    promocode = promocode.toUpperCase();

    if (!usedByUser) {
      this.usedPromocodesMap.set(userId, [promocode]);
      return;
    }

    if (usedByUser.includes(promocode)) {
      return;
    }

    this.usedPromocodesMap.set(
      userId,
      PRESENT_PROMOCODES.includes(promocode)
        ? [...usedByUser, promocode]
        : [promocode, ...usedByUser],
    );
  }

  removeUsedPromocode(code: string): void {
    const { isAuthorized, personalData } = this.userStore;
    if (!isAuthorized || !personalData.id) {
      return;
    }

    const { id } = personalData;
    const value = this.usedPromocodesMap.get(id);

    if (!value) {
      return;
    }

    const codeUpper = code.toUpperCase();

    if (!value.includes(codeUpper)) {
      return;
    }

    runInAction(() => {
      this.usedPromocodesMap.set(
        id,
        value.filter((item) => item !== code),
      );
    });
  }

  resetPromocode() {
    if (this.promocode.success === null) {
      return;
    }
    this.promocode = {
      coupon: null,
      value: '',
      errorType: null,
      message: '',
      success: null,
      discountLimit: null,
    };
    clearStoredPromocode();
  }

  validatePromocode(
    data: CheckPromocodeResponse,
    code: string,
  ): ReferralPromocode {
    const { cart, totalCartPrice } = this.catalogStore;

    if (!data.isActive) {
      return PromotionStore.generateErrorPromocode(
        code,
        'errors:promocodeNotFoundText',
      );
    }

    if (!data.applyToDiscountedItems) {
      const discountedItem = cart.filter(
        (item) => item.selected && Boolean(item.discountPrice),
      );

      if (discountedItem.length) {
        PromotionStore.generateErrorPromocode(
          code,
          'errors:promocodeDeniedDiscount',
          {
            names: discountedItem
              .map((item) => truncateItemName(item.name))
              .join(', '),
          },
        );
      }
    }

    const minimumPurchase = Number(
      mainStore.convertPenceToPounds(data.minimumPurchase),
    );

    if (
      minimumPurchase &&
      Number(totalCartPrice.base) > 0 &&
      Number(totalCartPrice.base) < minimumPurchase
    ) {
      return PromotionStore.generateErrorPromocode(
        code,
        'errors:promocodeMinimumPurchase',
        {
          amount: notSSRFormatPriceWithCurrency(minimumPurchase),
        },
      );
    }

    let deliveryMethod: ETADeliveryMethodType | undefined;

    if (data.code.toUpperCase() === PICKUP_ALCOHOL_COUPON_CODE) {
      if (!orderStore.isPickupAvailable) {
        return PromotionStore.generateErrorPromocode(
          code,
          'errors:promocodeNotFoundText',
        );
      }

      deliveryMethod = ETADeliveryMethodType.ClickAndCollect;
    }

    return {
      coupon: this._normalizeData(data),
      value: code,
      errorType: null,
      message: '',
      success: null,
      discountLimit: null,
      deliveryMethod,
    };
  }

  handleCalculateWithPromocodeResponse(
    errorCode: Nullable<string>,
    calculationNote: Nullable<'none' | 'partial' | 'all'>,
  ) {
    runInAction(() => {
      this.applyPromocodeMeta.setLoading(false);

      if (errorCode) {
        this.promocode.errorType = 'error';

        const translation = i18n.t(`errors:${errorCode}`, {
          count: this.promocode.coupon?.orderNumberRange[0] || 0,
          amount: notSSRFormatPriceWithCurrency(
            mainStore.convertPenceToPounds(
              this.promocode.coupon?.minimumPurchase || 0,
            ),
          ),
        });

        this.promocode.message =
          translation &&
          typeof translation === 'string' &&
          translation !== `errors:${errorCode}`
            ? translation
            : errorCode;
        this.promocode.success = false;
        return;
      }

      if (calculationNote === 'partial') {
        this.promocode.errorType = 'warning';
        this.promocode.message = t('promocodePartiallyApplied');
        this.promocode.success = true;
        return;
      }

      this.promocode.errorType = null;
      this.promocode.message = t('promocodeSuccessfullyApplied');
      this.promocode.success = true;
    });
  }

  loadStoredPromoCodes() {
    const storedPromocode = getStoredPromocode();

    if (!storedPromocode) {
      return;
    }

    const { code, customerId } = storedPromocode;

    if (
      this.userStore.personalData?.id &&
      customerId === this.userStore.personalData.id
    ) {
      return;
    }

    this.applyPromocode(code, ApplyPromocodeCallerSource.INTERNAL);
  }

  public setPromocodesTab(tab: PromocodesTab) {
    this.promocodesTab = tab;
  }

  public get activePromocodes() {
    return this._activePromocodesQuery.result.data;
  }

  public get isActivePromocodesLoading() {
    return this._activePromocodesQuery.result.isLoading;
  }

  public get inactivePromocodes() {
    return this._inactivePromocodesQuery.result.data;
  }

  public get isInactivePromocodesLoading() {
    return this._inactivePromocodesQuery.result.isLoading;
  }

  public get usedPromocodes() {
    return this._usedPromocodesQuery.result.data;
  }

  public get isUsedPromocodesLoading() {
    return this._usedPromocodesQuery.result.isLoading;
  }

  public get referralPromocodes(): Promocode[] {
    const data = this._rewardedPromocodesQuery.result.data ?? [];
    return data
      .slice()
      .sort((a, b) => (a.is_new === b.is_new ? 0 : a.is_new ? -1 : 1))
      .map(Promocode.fromRewarderPromoCode);
  }

  public get historyPromocodes(): Promocode[] {
    return [...(this.usedPromocodes ?? []), ...(this.inactivePromocodes ?? [])];
  }

  public get promocodes() {
    if (this.promocodesTab === PromocodesTab.AVAILABLE) {
      return this.activePromocodes ?? [];
    }
    if (this.promocodesTab === PromocodesTab.REFERRAL) {
      return this.referralPromocodes;
    }
    if (this.promocodesTab === PromocodesTab.HISTORY) {
      return this.historyPromocodes;
    }
    return [];
  }

  public get isPromocodesLoading() {
    if (this.promocodesTab === PromocodesTab.AVAILABLE) {
      return this.isActivePromocodesLoading;
    }
    if (this.promocodesTab === PromocodesTab.REFERRAL) {
      return this._rewardedPromocodesQuery.result.isLoading;
    }
    if (this.promocodesTab === PromocodesTab.HISTORY) {
      return this.isUsedPromocodesLoading || this.isInactivePromocodesLoading;
    }
    return false;
  }

  public get canLoadMorePromocodes() {
    if (this.promocodesTab === PromocodesTab.AVAILABLE) {
      return this._activePromocodesQuery.hasNextPage;
    }
    if (this.promocodesTab === PromocodesTab.REFERRAL) {
      return false;
    }
    if (this.promocodesTab === PromocodesTab.HISTORY) {
      return (
        this._usedPromocodesQuery.hasNextPage ||
        this._inactivePromocodesQuery.hasNextPage
      );
    }
    return false;
  }

  public get isFetchingNextPage() {
    if (this.promocodesTab === PromocodesTab.AVAILABLE) {
      return this._activePromocodesQuery.isFetchingNextPage;
    }
    if (this.promocodesTab === PromocodesTab.REFERRAL) {
      return false;
    }
    if (this.promocodesTab === PromocodesTab.HISTORY) {
      return (
        this._usedPromocodesQuery.isFetchingNextPage ||
        this._inactivePromocodesQuery.isFetchingNextPage
      );
    }
    return false;
  }

  public fetchNextPage() {
    if (this.promocodesTab === PromocodesTab.AVAILABLE) {
      return this._activePromocodesQuery.fetchNextPage();
    }
    if (this.promocodesTab === PromocodesTab.HISTORY) {
      const promises: Promise<unknown>[] = [];
      if (this._usedPromocodesQuery.hasNextPage) {
        promises.push(this._usedPromocodesQuery.fetchNextPage());
      }
      if (this._inactivePromocodesQuery.hasNextPage) {
        promises.push(this._inactivePromocodesQuery.fetchNextPage());
      }
      return Promise.all(promises);
    }
  }

  private _normalizeData(data: CheckPromocodeResponse): CheckPromocodeResponse {
    if (data.type === 'FIXED' && data.value && !data.minimumPurchase) {
      return {
        ...data,
        minimumPurchase: data.value,
      };
    }

    return data;
  }

  private _emitUsedPromocodeEvent() {
    const { cart, cartForFirebase, totalCartPrice, finalPrice } =
      this.catalogStore;

    analyticsService.track(AnalyticsEvent.SELECT_PROMOTION, {
      items: cartForFirebase,
      location_id: '',
    });

    analyticsService.track(AnalyticsEvent.PURCHASE_PROMOCODE_ENTERED, {
      ...this._getAnalyticsInstance(),
      success: true,
      products_amount: cart.reduce((sum, item) => sum + item.count, 0),
      items_amount: cart.length,
      price: totalCartPrice.base,
      final_price: finalPrice,
      eta_min: orderStore.etaCalculation?.duration.min || 0,
      eta_max: orderStore.etaCalculation?.duration.max || 0,
      delivery_fee: orderStore.fee.shippingPounds || 0,
      threshold: orderStore.fee.thresholdPounds || 0,
      is_surger: orderStore.etaCalculation?.highDemand || false,
    });
  }

  private _getAnalyticsInstance(): Record<string, unknown> {
    const { value, coupon } = this.promocode;

    if (!coupon) {
      return { code: value };
    }

    return {
      code: value,
      discount:
        coupon.type === 'FIXED'
          ? mainStore.convertPenceToPounds(coupon.value || 0)
          : coupon.value || 0,
      type: coupon.type || '',
    };
  }

  private _handleError(
    code: string,
    error: unknown,
    source: ApplyPromocodeCallerSource,
  ): ReferralPromocode {
    if (!(error instanceof AxiosError)) {
      return PromotionStore.generateErrorPromocode(code);
    }

    const { response } = error;
    const errorCode = response?.data?.data?.err_code;

    if (!errorCode) {
      return PromotionStore.generateErrorPromocode(code);
    }

    if (source === ApplyPromocodeCallerSource.CHECKOUT) {
      return PromotionStore.generateErrorPromocode(
        code,
        errorCode === 'DISCOUNT.COUPONS.ONLY_FIRST_ORDER_ALLOWED'
          ? 'errors:notFirstOrderPromocodeFreeDelivery'
          : 'errors:promocodeNotFoundText',
      );
    }

    return PromotionStore.generateErrorPromocode(code, `errors:${errorCode}`);
  }

  destroy() {
    this.applyPromocodeMeta.destroy();
    this._abortController.abort();
  }
}
